'use client';

import { useState, FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import Icon from '@/components/ui/AppIcon';

interface CheckoutFormProps {
  clientSecret: string | null;
  onPaymentSuccess: (paymentIntent: PaymentIntent) => void;
}

export default function CheckoutForm({ clientSecret, onPaymentSuccess }: CheckoutFormProps) {
  const router = useRouter();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate email
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      console.log('[Checkout] Starting payment process...');
      
      // Submit the form to collect all payment details
      console.log('[Checkout] Submitting payment elements...');
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('[Checkout] Elements submit error:', submitError);
        throw new Error(submitError.message || 'Failed to validate payment details');
      }
      console.log('[Checkout] Payment elements submitted successfully');

      // Update the payment intent with customer email
      console.log('[Checkout] Updating payment intent with email...');
      const updateResponse = await fetch('/api/update-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientSecret,
          email,
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        console.error('[Checkout] Update payment intent failed:', errorData);
        throw new Error(errorData.error || 'Failed to update payment information');
      }
      console.log('[Checkout] Payment intent updated successfully');

      // Confirm the payment with all collected details
      console.log('[Checkout] Confirming payment...');
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
          receipt_email: email,
          payment_method_data: {
            billing_details: {
              email: email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        console.error('[Checkout] Payment confirmation failed:', error);
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('[Checkout] Payment succeeded:', paymentIntent.id);
        onPaymentSuccess(paymentIntent);
      } else {
        console.error('[Checkout] Payment incomplete:', paymentIntent?.status);
        setErrorMessage('Payment processing incomplete. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('[Checkout] Payment error:', err);
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-foreground">Preparing checkout...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Email Section */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="EnvelopeIcon" size={20} className="text-primary" />
          Delivery Email
        </h3>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleEmailChange}
            className={`w-full px-4 py-3 border ${
              emailError ? 'border-error' : 'border-border'
            } rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all`}
            placeholder="your.email@example.com"
            required
          />
          {emailError && (
            <p className="text-sm text-error mt-1 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {emailError}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Your eBook will be delivered to this email address
          </p>
        </div>
      </div>

      {/* Payment Information */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CreditCardIcon" size={20} className="text-primary" />
          Payment Information
        </h3>
        <div className="bg-card border border-border rounded-xl p-6">
          <PaymentElement
            options={{
              layout: 'tabs',
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'never',
                  phone: 'auto',
                  address: {
                    line1: 'auto',
                    line2: 'auto',
                    city: 'auto',
                    state: 'auto',
                    country: 'auto',
                    postalCode: 'auto',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-error/10 border border-error rounded-xl p-4 flex items-start gap-3">
          <Icon name="ExclamationCircleIcon" size={20} className="text-error shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errorMessage}</p>
        </div>
      )}

      {/* Security Notice */}
      <div className="bg-secondary rounded-xl p-4 flex items-start gap-3">
        <Icon name="LockClosedIcon" size={20} className="text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-foreground font-semibold mb-1">Secure Payment</p>
          <p className="text-xs text-muted-foreground">
            Your payment information is encrypted and secure. We never store your card details.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <span>Processing Payment...</span>
          </>
        ) : (
          <>
            <Icon name="LockClosedIcon" size={20} />
            <span>Complete Secure Payment</span>
          </>
        )}
      </button>

      {/* Money-Back Guarantee */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Icon name="ShieldCheckIcon" size={16} className="text-primary" />
          30-day money-back guarantee • Instant delivery
        </p>
      </div>
    </form>
  );
}